import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  listLevyReports, getLevyReport, generateLevyReport,
  markLevyReportSubmitted, acknowledgeLevyReport,
  markLevyCsvExported, exportLevyReportToCsv,
} from '../../services/complianceService';
import toast from 'react-hot-toast';

// Format YYYY-MM → "May 2026"
function formatMonth(monthKey) {
  if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) return monthKey || '';
  const [y, m] = monthKey.split('-');
  const date = new Date(parseInt(y), parseInt(m) - 1, 1);
  return date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
}

function formatTs(ts) {
  if (!ts) return '';
  const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-AU');
}

function statusBadge(status) {
  switch (status) {
    case 'draft':        return { bg: '#fef3c7', color: '#92400e', label: 'Draft' };
    case 'submitted':    return { bg: '#dbeafe', color: '#1e40af', label: 'Submitted' };
    case 'acknowledged': return { bg: 'rgba(45,212,168,0.1)', color: '#059669', label: 'Acknowledged' };
    default:             return { bg: '#f1f5f9', color: '#5a6578', label: status };
  }
}

// Compute candidate months for manual generation: current month + 12 previous
function candidateMonths() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 13; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

export default function AdminLevyPanel() {
  const navigate = useNavigate();
  const { monthKey: routeMonthKey } = useParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedMonthKey, setSelectedMonthKey] = useState(routeMonthKey || null);
  const [generating, setGenerating] = useState(false);
  const [manualMonth, setManualMonth] = useState('');
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [commissionerRef, setCommissionerRef] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);

  const loadList = () => {
    setLoading(true);
    listLevyReports()
      .then(res => setReports(res.reports || []))
      .catch(err => toast.error(err.message || 'Could not load reports'))
      .finally(() => setLoading(false));
  };

  const loadDetail = (monthKey) => {
    setSelectedReport(null);
    setSelectedMonthKey(monthKey);
    getLevyReport(monthKey)
      .then(res => setSelectedReport(res.report))
      .catch(err => toast.error(err.message));
  };

  useEffect(() => { loadList(); }, []);
  useEffect(() => {
    if (routeMonthKey) loadDetail(routeMonthKey);
  }, [routeMonthKey]);

  const handleGenerate = async (monthKey) => {
    if (!monthKey) { toast.error('Pick a month'); return; }
    setGenerating(true);
    try {
      const result = await generateLevyReport(monthKey);
      toast.success(`Report ${formatMonth(monthKey)} generated: ${result.totalTrips} trips, $${result.totalLevyOwed.toFixed(2)} levy owed`);
      loadList();
      loadDetail(monthKey);
      navigate(`/admin/levy/${monthKey}`);
    } catch (err) {
      toast.error(err.message || 'Generation failed');
    }
    setGenerating(false);
  };

  const handleExportCsv = () => {
    if (!selectedReport) return;
    try {
      const result = exportLevyReportToCsv(selectedReport);
      toast.success(`Exported ${result.rowCount} rows to CSV`);
      markLevyCsvExported(selectedReport.monthKey).catch(() => {});
      loadDetail(selectedReport.monthKey);
    } catch (err) {
      toast.error(err.message || 'CSV export failed');
    }
  };

  const handleMarkSubmitted = async () => {
    if (!selectedReport) return;
    if (!window.confirm(`Mark ${formatMonth(selectedReport.monthKey)} report as submitted to NSW Commissioner? This is irreversible — all ${selectedReport.totalTrips} trips will be stamped 'remitted'.`)) return;
    setActionInProgress(true);
    try {
      const result = await markLevyReportSubmitted(selectedReport.monthKey, submissionNotes);
      toast.success(`Marked submitted. ${result.tripsStamped} trips stamped.`);
      setSubmissionNotes('');
      loadList();
      loadDetail(selectedReport.monthKey);
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
    setActionInProgress(false);
  };

  const handleAcknowledge = async () => {
    if (!selectedReport) return;
    if (!commissionerRef.trim()) { toast.error('Enter Commissioner receipt number'); return; }
    setActionInProgress(true);
    try {
      await acknowledgeLevyReport(selectedReport.monthKey, commissionerRef);
      toast.success(`Acknowledged with receipt ${commissionerRef}`);
      setCommissionerRef('');
      loadList();
      loadDetail(selectedReport.monthKey);
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
    setActionInProgress(false);
  };

  return (
    <div className="max-w-[1100px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/admin')}
          className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Admin dashboard</button>
      </div>

      <h1 className="font-serif mt-0 mb-2">
        NSW Passenger Service Levy Reports
      </h1>
      <p className="text-body text-[0.9rem] mb-4">
        Monthly aggregation of trips with levy owed. Auto-generated on the 1st of each month for the prior month.
        [VERIFY: NSW Point to Point Commissioner — confirm submission deadline, format, and method]
      </p>

      {/* Manual generate */}
      <div className="p-3.5 rounded-xl bg-white border border-slate-200 mb-6">
        <div className="font-semibold text-brand-dark mb-2">Manually generate or regenerate</div>
        <div className="flex gap-2 items-center flex-wrap">
          <select className="form-input form-select flex-1 min-w-[200px]" value={manualMonth}
            onChange={e => setManualMonth(e.target.value)}>
            <option value="">Pick a month...</option>
            {candidateMonths().map(m => (
              <option key={m} value={m}>{formatMonth(m)}</option>
            ))}
          </select>
          <button onClick={() => handleGenerate(manualMonth)}
            disabled={!manualMonth || generating}
            className="px-4 py-2.5 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-semibold">{generating ? 'Generating...' : 'Generate report'}</button>
        </div>
        <div className="text-[0.78rem] text-muted mt-1.5">
          Note: regenerating a 'draft' report refreshes its data. Submitted/acknowledged reports cannot be regenerated.
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid gap-4 items-start" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.5fr)' }}>
        {/* LEFT: report list */}
        <div>
          <h3 className="mt-0 mb-3 text-brand-dark">All reports</h3>
          {loading && <div className="p-5 text-center text-muted">Loading...</div>}
          {!loading && reports.length === 0 && (
            <div className="p-5 text-center text-muted bg-white rounded-xl border border-slate-200 text-[0.85rem]">
              No reports yet. The first will auto-generate on the 1st of next month.
            </div>
          )}
          <div className="grid gap-2">
            {reports.map(r => {
              const badge = statusBadge(r.status);
              const isSelected = selectedMonthKey === r.monthKey;
              return (
                <div key={r.id} onClick={() => { navigate(`/admin/levy/${r.monthKey}`); }}
                  className="p-3 rounded-[10px] cursor-pointer"
                  style={{
                    background: isSelected ? 'rgba(45,212,168,0.06)' : '#fff',
                    border: `1.5px solid ${isSelected ? '#2dd4a8' : '#e2e8f0'}`,
                  }}>
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-bold text-brand-dark">{formatMonth(r.monthKey)}</span>
                    <span className="px-2 py-0.5 rounded-full text-[0.7rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
                      {badge.label}
                    </span>
                  </div>
                  <div className="text-[0.78rem] text-body">
                    {r.totalTrips} trips · ${(r.totalLevyOwed || 0).toFixed(2)} owed
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT: selected report detail */}
        <div>
          {!selectedReport && (
            <div className="p-10 text-center text-muted bg-white rounded-xl border border-slate-200">
              {selectedMonthKey ? 'Loading...' : 'Select a report to view detail'}
            </div>
          )}

          {selectedReport && (() => {
            const badge = statusBadge(selectedReport.status);
            return (
              <div>
                <div className="p-4 rounded-xl bg-white border border-slate-200 mb-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h2 className="m-0 font-serif">
                        {formatMonth(selectedReport.monthKey)}
                      </h2>
                      <div className="text-[0.78rem] text-muted mt-0.5">
                        Generated {formatTs(selectedReport.generatedAt)}
                      </div>
                    </div>
                    <span className="px-3 py-1 rounded-full text-[0.78rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
                      {badge.label}
                    </span>
                  </div>

                  {/* Summary numbers */}
                  <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                    <div className="p-2.5 rounded-lg bg-slate-50">
                      <div className="text-[0.7rem] text-muted uppercase font-semibold">Trips</div>
                      <div className="font-bold text-[1.1rem] text-brand-dark">{selectedReport.totalTrips}</div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-slate-50">
                      <div className="text-[0.7rem] text-muted uppercase font-semibold">Levy owed</div>
                      <div className="font-bold text-[1.1rem] text-brand-dark">${(selectedReport.totalLevyOwed || 0).toFixed(2)}</div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-slate-50">
                      <div className="text-[0.7rem] text-muted uppercase font-semibold">Fares (excl levy)</div>
                      <div className="font-bold text-[1.1rem] text-brand-dark">${(selectedReport.totalFareExclLevy || 0).toFixed(2)}</div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-slate-50">
                      <div className="text-[0.7rem] text-muted uppercase font-semibold">Levy collected</div>
                      <div className="font-bold text-[1.1rem] text-brand-dark">{selectedReport.tripsWithCollectedLevy} / {selectedReport.totalTrips}</div>
                    </div>
                  </div>

                  {selectedReport.tripsWithPendingPayment > 0 && (
                    <div className="p-2.5 rounded-lg text-[0.82rem] mb-3" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
                      ⚠ {selectedReport.tripsWithPendingPayment} trip{selectedReport.tripsWithPendingPayment !== 1 ? 's' : ''} have not yet been paid by the rider.
                      Levy is still owed to the Commissioner regardless of rider payment status.
                    </div>
                  )}

                  {/* Lifecycle history */}
                  {selectedReport.submittedAt && (
                    <div className="text-[0.82rem] text-body mb-1">
                      <strong>Submitted:</strong> {formatTs(selectedReport.submittedAt)}
                      {selectedReport.submissionNotes && <div className="italic mt-0.5">Notes: {selectedReport.submissionNotes}</div>}
                    </div>
                  )}
                  {selectedReport.acknowledgedAt && (
                    <div className="text-[0.82rem] text-body mb-1">
                      <strong>Acknowledged:</strong> {formatTs(selectedReport.acknowledgedAt)} ·
                      Receipt: <span className="font-mono font-bold">{selectedReport.commissionerReceiptNumber}</span>
                    </div>
                  )}
                  {selectedReport.csvExportedAt && (
                    <div className="text-[0.82rem] text-body">
                      <strong>CSV last exported:</strong> {formatTs(selectedReport.csvExportedAt)}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap mb-4">
                  <button onClick={handleExportCsv}
                    className="px-4 py-2.5 rounded-[10px] border-[1.5px] border-brand-primary bg-white text-brand-primary cursor-pointer font-semibold">📥 Export CSV</button>

                  {selectedReport.status === 'draft' && (
                    <button onClick={() => handleGenerate(selectedReport.monthKey)}
                      disabled={generating}
                      className="px-4 py-2.5 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">🔄 Regenerate</button>
                  )}
                </div>

                {/* Submit / Acknowledge controls */}
                {selectedReport.status === 'draft' && (
                  <div className="p-4 rounded-xl bg-white border border-slate-200 mb-4">
                    <h4 className="mt-0 mb-2">Mark as submitted to NSW Commissioner</h4>
                    <p className="text-[0.82rem] text-body mb-2">
                      After submitting to the Commissioner (via their portal/API/email — [VERIFY: confirm method]),
                      record the submission here. This stamps all {selectedReport.totalTrips} trips as 'remitted'.
                    </p>
                    <textarea className="form-input mb-2" rows={2} placeholder="Submission notes (optional — e.g. portal reference)"
                      value={submissionNotes} onChange={(e) => setSubmissionNotes(e.target.value)} />
                    <button onClick={handleMarkSubmitted} disabled={actionInProgress}
                      className="px-4 py-2.5 rounded-[10px] border-0 text-white cursor-pointer font-semibold"
                      style={{ background: '#4f46e5' }}>{actionInProgress ? '...' : 'Mark as submitted'}</button>
                  </div>
                )}

                {selectedReport.status === 'submitted' && (
                  <div className="p-4 rounded-xl bg-white border border-slate-200 mb-4">
                    <h4 className="mt-0 mb-2">Record Commissioner acknowledgement</h4>
                    <p className="text-[0.82rem] text-body mb-2">
                      Enter the receipt/reference number the Commissioner provided.
                    </p>
                    <input className="form-input mb-2" placeholder="Commissioner receipt number"
                      value={commissionerRef} onChange={(e) => setCommissionerRef(e.target.value)} />
                    <button onClick={handleAcknowledge} disabled={actionInProgress || !commissionerRef.trim()}
                      className="px-4 py-2.5 rounded-[10px] border-0 bg-emerald-600 text-white cursor-pointer font-semibold">{actionInProgress ? '...' : 'Record acknowledgement'}</button>
                  </div>
                )}

                {/* Trip preview table */}
                {selectedReport.trips && selectedReport.trips.length > 0 && (
                  <div className="p-0 rounded-xl bg-white border border-slate-200 overflow-hidden">
                    <div className="p-3 font-bold text-brand-dark border-b border-slate-200">
                      Trips in this report ({selectedReport.trips.length})
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      <table className="w-full border-collapse text-[0.82rem]">
                        <thead className="sticky top-0 bg-slate-50">
                          <tr className="text-left">
                            <th className="px-3 py-2 border-b border-slate-200 font-semibold text-body">Ref</th>
                            <th className="px-3 py-2 border-b border-slate-200 font-semibold text-body">Date</th>
                            <th className="px-3 py-2 border-b border-slate-200 font-semibold text-body">Driver</th>
                            <th className="px-3 py-2 border-b border-slate-200 font-semibold text-body text-right">Fare</th>
                            <th className="px-3 py-2 border-b border-slate-200 font-semibold text-body text-right">Levy</th>
                            <th className="px-3 py-2 border-b border-slate-200 font-semibold text-body">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedReport.trips.map(t => (
                            <tr key={t.bookingId} className="border-b border-slate-100">
                              <td className="px-3 py-2 font-mono">{t.referenceNumber}</td>
                              <td className="px-3 py-2">{formatTs(t.completedAt).split(',')[0] || ''}</td>
                              <td className="px-3 py-2 font-mono text-[0.78rem]">
                                {t.driverAuthNumber || <span className="text-red-600">[no PT]</span>}
                              </td>
                              <td className="px-3 py-2 text-right">${(t.fareTotal || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-right">${(t.levyAmount || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-[0.75rem] text-body">{t.levyStatus}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}